/**
 * User Punchh Tab Component
 *
 * Displays Punchh account information including balance, rewards, and membership.
 * Allows creating a new Punchh account if not linked.
 */

import { useState } from 'react';
import { Plus, Loader2, Eye, EyeOff, Copy, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import {
  usePunchhInfo,
  useCreatePunchhAccount,
  useRegeneratePunchhTokens,
} from '@/hooks/use-user-management';
import type { User } from '@/types/user-management';

interface UserPunchhTabProps {
  user: User;
}

/**
 * Format Date
 * Formats an ISO date string to a readable format
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Mask Token
 * Masks a token showing only the last 4 characters
 */
function maskToken(token: string): string {
  if (token.length <= 4) {
    return '****';
  }
  return '****' + token.slice(-4);
}

/**
 * User Punchh Tab
 * Displays Punchh account information and account creation
 */
export function UserPunchhTab({ user }: UserPunchhTabProps) {
  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);
  const [isTokenVisible, setIsTokenVisible] = useState(false);

  const { data: punchhInfo, isLoading, error } = usePunchhInfo(user.punchId ? user.id : null);

  /**
   * Toggle token visibility and log the action
   */
  const handleToggleTokenVisibility = () => {
    if (!isTokenVisible) {
      console.info(
        '[Audit] Access token viewed for user:',
        user.id,
        'at',
        new Date().toISOString()
      );
    }
    setIsTokenVisible(prev => !prev);
  };

  /**
   * Copy token to clipboard
   */
  const handleCopyToken = async () => {
    if (user.punchhAccessToken) {
      await navigator.clipboard.writeText(user.punchhAccessToken);
      console.info(
        '[Audit] Access token copied for user:',
        user.id,
        'at',
        new Date().toISOString()
      );
      toast({
        title: 'Copied',
        description: 'Access token copied to clipboard',
      });
    }
  };

  const createPunchhAccountMutation = useCreatePunchhAccount();
  const regenerateTokensMutation = useRegeneratePunchhTokens();

  const hasPunchhAccount = !!user.punchId;

  /**
   * Handle Create Punchh Account
   */
  const handleCreatePunchhAccount = async () => {
    // Validate required fields
    if (!user.firstName || !user.lastName || !user.email || !user.phone) {
      toast({
        title: 'Missing required fields',
        description:
          'User must have firstName, lastName, email, and phone to create a Punchh account.',
        variant: 'destructive',
      });
      setIsDialogOpen(false);
      return;
    }

    try {
      await createPunchhAccountMutation.mutateAsync(user.id);
      toast({
        title: 'Success',
        description: 'Punchh account has been created successfully',
      });
      setIsDialogOpen(false);
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create Punchh account',
        variant: 'destructive',
      });
    }
  };

  /**
   * Handle Regenerate Punchh Tokens
   */
  const handleRegeneratePunchhTokens = async () => {
    try {
      const result = await regenerateTokensMutation.mutateAsync(user.id);
      const updated = [];
      if (result.tokensUpdated.punchhAuthToken) updated.push('authToken');
      if (result.tokensUpdated.punchhAccessToken) updated.push('accessToken');

      toast({
        title: 'Success',
        description:
          updated.length > 0
            ? `Regenerated: ${updated.join(', ')}`
            : 'Tokens verified — no changes needed',
      });
      setIsRegenerateDialogOpen(false);
    } catch (error: unknown) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to regenerate Punchh tokens',
        variant: 'destructive',
      });
    }
  };

  /**
   * Check if user can create Punchh account
   */
  const canCreatePunchhAccount =
    !hasPunchhAccount && user.firstName && user.lastName && user.email && user.phone;

  return (
    <div className="space-y-6">
      {/* Header with Status */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Punchh Account</h3>
        <Badge variant={hasPunchhAccount ? 'success' : 'secondary'}>
          {hasPunchhAccount ? 'Linked' : 'Not Linked'}
        </Badge>
      </div>

      {/* Not Linked State */}
      {!hasPunchhAccount && (
        <div className="space-y-4">
          <Alert>
            <AlertDescription>
              This user does not have a linked Punchh account. You can create one using the button
              below.
            </AlertDescription>
          </Alert>

          {!canCreatePunchhAccount && (
            <Alert variant="destructive">
              <AlertDescription>
                User is missing required fields (firstName, lastName, email, phone) to create a
                Punchh account.
              </AlertDescription>
            </Alert>
          )}

          <Button
            onClick={() => setIsDialogOpen(true)}
            disabled={!canCreatePunchhAccount || createPunchhAccountMutation.isPending}
            className="w-full"
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Punchh Account
          </Button>
        </div>
      )}

      {/* Linked State - Loading */}
      {hasPunchhAccount && isLoading && (
        <div className="flex flex-col items-center justify-center py-12 space-y-2">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Loading Punchh account information...</p>
        </div>
      )}

      {/* Linked State - Error */}
      {hasPunchhAccount && error && (
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load Punchh account information: {error.message}
          </AlertDescription>
        </Alert>
      )}

      {/* Linked State - Data */}
      {hasPunchhAccount && punchhInfo && punchhInfo.linked && (
        <div className="space-y-4">
          {/* Account Information */}
          <div className="space-y-1">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold">Account Information</h4>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsRegenerateDialogOpen(true)}
                disabled={regenerateTokensMutation.isPending}
              >
                {regenerateTokensMutation.isPending ? (
                  <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-3.5 w-3.5" />
                )}
                Regenerate Tokens
              </Button>
            </div>
            <div className="rounded-lg border p-4 space-y-2">
              <div className="flex justify-between py-1 border-b">
                <span className="text-sm font-medium text-muted-foreground">Punchh ID</span>
                <span className="text-sm font-mono">{punchhInfo.userInfo?.id || 'N/A'}</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span className="text-sm font-medium text-muted-foreground">User ID</span>
                <span className="text-sm font-mono">{punchhInfo.userInfo?.userId}</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span className="text-sm font-medium text-muted-foreground">User Code</span>
                <span className="text-sm font-mono">{punchhInfo.userInfo?.userCode}</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span className="text-sm font-medium text-muted-foreground">Access Token</span>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-mono break-all max-w-[150px] text-right">
                    {user.punchhAccessToken
                      ? isTokenVisible
                        ? user.punchhAccessToken
                        : maskToken(user.punchhAccessToken)
                      : 'N/A'}
                  </span>
                  {user.punchhAccessToken && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={handleToggleTokenVisibility}
                        title={isTokenVisible ? 'Hide token' : 'Show token'}
                      >
                        {isTokenVisible ? (
                          <EyeOff className="h-3.5 w-3.5" />
                        ) : (
                          <Eye className="h-3.5 w-3.5" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={handleCopyToken}
                        title="Copy token"
                      >
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span className="text-sm font-medium text-muted-foreground">Email</span>
                <span className="text-sm">{punchhInfo.userInfo?.email}</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span className="text-sm font-medium text-muted-foreground">Name</span>
                <span className="text-sm">
                  {punchhInfo.userInfo?.firstName} {punchhInfo.userInfo?.lastName}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span className="text-sm font-medium text-muted-foreground">Phone</span>
                <span className="text-sm">{punchhInfo.userInfo?.phone}</span>
              </div>
              <div className="flex justify-between py-1 border-b">
                <span className="text-sm font-medium text-muted-foreground">Birthday</span>
                <span className="text-sm">
                  {punchhInfo.userInfo?.birthday ? formatDate(punchhInfo.userInfo.birthday) : 'N/A'}
                </span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-sm font-medium text-muted-foreground">Created At</span>
                <span className="text-sm">
                  {punchhInfo.userInfo?.createdAt
                    ? formatDate(punchhInfo.userInfo.createdAt)
                    : 'N/A'}
                </span>
              </div>
            </div>
          </div>

          {/* Balance Information */}
          {punchhInfo.balance?.accountBalance ? (
            <div className="space-y-1">
              <h4 className="text-sm font-semibold mb-2">Balance</h4>
              <div className="rounded-lg border p-4 space-y-2">
                <div className="flex justify-between py-1 border-b">
                  <span className="text-sm font-medium text-muted-foreground">Points</span>
                  <span className="text-sm font-semibold">
                    {punchhInfo.balance.accountBalance.redeemablePoints ?? 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-b">
                  <span className="text-sm font-medium text-muted-foreground">Lifetime Points</span>
                  <span className="text-sm">
                    {punchhInfo.balance.accountBalance.lifetimePoints ?? 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-b">
                  <span className="text-sm font-medium text-muted-foreground">Expiring Points</span>
                  <span className="text-sm">
                    {punchhInfo.balance.accountBalance.expiringBankedCurrency ?? 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-sm font-medium text-muted-foreground">Expiring Date</span>
                  <span className="text-sm">
                    {punchhInfo.balance.accountBalance.bankedCurrencyExpiringOn
                      ? formatDate(punchhInfo.balance.accountBalance.bankedCurrencyExpiringOn)
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <Alert>
              <AlertDescription>
                Balance information is not available at this time.
              </AlertDescription>
            </Alert>
          )}

          {/* Rewards Information */}
          {punchhInfo.balance &&
          (punchhInfo.balance.rewards ||
            punchhInfo.balance.coupons ||
            punchhInfo.balance.punchCards) ? (
            <div className="space-y-1">
              <h4 className="text-sm font-semibold mb-2">Rewards</h4>
              <div className="rounded-lg border p-4 space-y-2">
                <div className="flex justify-between py-1 border-b">
                  <span className="text-sm font-medium text-muted-foreground">
                    Available Rewards
                  </span>
                  <Badge variant="success">{punchhInfo.balance.rewards?.length ?? 0}</Badge>
                </div>
                <div className="flex justify-between py-1 border-b">
                  <span className="text-sm font-medium text-muted-foreground">Active Coupons</span>
                  <span className="text-sm">{punchhInfo.balance.coupons?.length ?? 0}</span>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-sm font-medium text-muted-foreground">Punch Cards</span>
                  <span className="text-sm">{punchhInfo.balance.punchCards?.length ?? 0}</span>
                </div>
              </div>
            </div>
          ) : (
            <Alert>
              <AlertDescription>
                Rewards information is not available at this time.
              </AlertDescription>
            </Alert>
          )}

          {/* Membership Information */}
          {punchhInfo.balance?.accountBalance?.currentMembershipLevelName ? (
            <div className="space-y-1">
              <h4 className="text-sm font-semibold mb-2">Membership</h4>
              <div className="rounded-lg border p-4 space-y-2">
                <div className="flex justify-between py-1 border-b">
                  <span className="text-sm font-medium text-muted-foreground">Level</span>
                  <Badge>
                    {punchhInfo.balance.accountBalance.currentMembershipLevelName ?? 'N/A'}
                  </Badge>
                </div>
                <div className="flex justify-between py-1">
                  <span className="text-sm font-medium text-muted-foreground">Level ID</span>
                  <span className="text-sm">
                    {punchhInfo.balance.accountBalance.currentMembershipLevelId ?? 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <Alert>
              <AlertDescription>
                Membership information is not available at this time.
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Create Punchh Account Dialog */}
      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create Punchh Account</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will create a new Punchh account for this user with the following information:
              </p>
              <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                <div>
                  <strong>Name:</strong> {user.firstName} {user.lastName}
                </div>
                <div>
                  <strong>Email:</strong> {user.email}
                </div>
                <div>
                  <strong>Phone:</strong> {user.phone}
                </div>
                {user.dateOfBirth && (
                  <div>
                    <strong>Birthday:</strong> {user.dateOfBirth}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={createPunchhAccountMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCreatePunchhAccount}
              disabled={createPunchhAccountMutation.isPending}
            >
              {createPunchhAccountMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Account'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Regenerate Punchh Tokens Dialog */}
      <AlertDialog open={isRegenerateDialogOpen} onOpenChange={setIsRegenerateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate Punchh Tokens</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This will reset the user&apos;s Punchh password and sign in via Mobile API to obtain
                fresh tokens. The user&apos;s existing Punchh password will be changed.
              </p>
              <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                <div>
                  <strong>User:</strong> {user.firstName} {user.lastName}
                </div>
                <div>
                  <strong>Email:</strong> {user.email}
                </div>
                <div>
                  <strong>Punchh ID:</strong> {user.punchId}
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={regenerateTokensMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRegeneratePunchhTokens}
              disabled={regenerateTokensMutation.isPending}
            >
              {regenerateTokensMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Regenerating...
                </>
              ) : (
                'Regenerate Tokens'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
